/**
 * The steps THIS user's rail runs.
 *
 * Called ONCE, at beginRail, and the result is persisted as rail.stepIds. Every
 * index and total in the system is then computed against the stored list, not
 * against RAIL_STEPS — the same discipline the demo guide learned the hard way
 * (its docstring records a run that could never finish because the cursor was
 * counted against the full list while the visible set was shorter).
 *
 * Unlike the demo's visibleSteps this must NOT be re-run per read: hasLiveAccess
 * derives from a Keycloak token that can change mid-session, and a visible set
 * that shifted under a live cursor would leave `step` pointing at a different
 * step. See OnboardingRailState in ../types.ts.
 */

import { RAIL_STEPS, type RailStep } from "./steps";

export function visibleRailSteps(input: {
  /**
   * Live product access: authenticated, not expired, and either internal or
   * holding the Keycloak `subscriber` role.
   *
   * Worth knowing why this is checked at all when onboarding already requires a
   * trial. The two facts come from different systems — onboarding eligibility
   * reads Chargebee (`in_trial`), live access reads Keycloak — and when they
   * disagree, the user lands in demo mode, where the squawk greys out every
   * channel and Tawk serves the sales property instead of the analyst desk.
   * Both of those steps would then ask for something the product refuses.
   *
   * One boolean covering both steps, rather than per-channel role arithmetic: a
   * subscriber missing an individual squawk role still sees an explicable lock
   * icon and can Skip, which is a normal outcome rather than a dead end.
   */
  hasLiveAccess: boolean;
}): RailStep[] {
  return RAIL_STEPS.filter(
    (step) => !step.requiresLiveAccess || input.hasLiveAccess
  );
}
